import { NOOP } from "openchat-shared";
import { untrack } from "svelte";
let pauseCount = 0;
// Callbacks to publish dirty values from writable stores
const publishesPending = [];
// Callbacks to push new values to their subscribers
let subscriptionsPending = [];
// Callbacks to retry syncing derived stores whose dependencies were dirty when last attempted
let derivedStoresToRetry = [];
export function withPausedStores(fn) {
    try {
        pauseCount++;
        return fn();
    }
    finally {
        if (pauseCount === 1) {
            // Publish all changes to writable stores
            executeCallbacks(publishesPending);
            // Unpause
            pauseCount = 0;
            // Run the derived store subscriptions
            runSubscriptions();
        }
        else {
            pauseCount--;
        }
    }
}
function runSubscriptions() {
    while (subscriptionsPending.length > 0) {
        // Execute all pending subscription callbacks
        executeCallbacks(subscriptionsPending);
        // Once the subscriptions are processed, queue up any derived stores which need to be retried and loop again
        subscriptionsPending = derivedStoresToRetry;
        derivedStoresToRetry = [];
    }
}
function executeCallbacks(callbacks) {
    for (let index = 0; index < callbacks.length; index++) {
        callbacks[index]();
    }
    callbacks.length = 0;
}
export function writable(value, start, equalityCheck) {
    return new _Writable(value, start, equalityCheck);
}
export function readable(value, start, equalityCheck) {
    const store = writable(value, start, equalityCheck);
    return {
        subscribe: store.subscribe,
        value: store.value,
        dirty: store.dirty,
    };
}
export function derived(stores, fn, equalityCheck) {
    return new _Derived(stores, fn, equalityCheck ?? ((a, b) => a === b));
}
export function get(store) {
    return store.value;
}
class _Writable {
    #subscriptions = new Map();
    #start;
    #equalityCheck;
    #value;
    #publishPending = false;
    #started = false;
    #stop = undefined;
    constructor(initValue, start, equalityCheck) {
        this.#value = initValue;
        this.#start = start;
        this.#equalityCheck = equalityCheck ?? ((a, b) => a === b);
    }
    subscribe(subscriber, invalidate) {
        const id = Symbol();
        this.#subscriptions.set(id, [subscriber, invalidate]);
        if (this.#subscriptions.size === 1) {
            if (this.#start !== undefined) {
                const stop = this.#start(this.set, this.update);
                if (typeof stop === "function") {
                    this.#stop = stop;
                }
            }
            this.#started = true;
        }
        subscriber(this.#value);
        return () => this.#unsubscribe(id);
    }
    get value() {
        return this.#value;
    }
    set(newValue) {
        if (this.#started && this.#equalityCheck(newValue, this.#value)) {
            return;
        }
        this.#value = newValue;
        if (pauseCount === 0 || !this.#started) {
            this.#publishPending = true;
            this.#publish();
        }
        else {
            if (!this.#publishPending) {
                // Register callback to publish the new value once stores are unpaused
                publishesPending.push(() => this.#publish());
                this.#publishPending = true;
            }
        }
    }
    update(updateFn) {
        const newValue = updateFn(this.#value);
        this.set(newValue);
    }
    get dirty() {
        return this.#publishPending;
    }
    #publish() {
        if (this.#publishPending) {
            this.#publishPending = false;
            if (this.#started) {
                const shouldRunSubscriptions = pauseCount === 0 && subscriptionsPending.length === 0;
                for (const [subscription, invalidate] of this.#subscriptions.values()) {
                    invalidate?.();
                    subscriptionsPending.push(() => subscription(this.#value));
                }
                if (shouldRunSubscriptions) {
                    runSubscriptions();
                }
            }
        }
    }
    #unsubscribe(id) {
        this.#subscriptions.delete(id);
        if (this.#subscriptions.size === 0) {
            if (typeof this.#stop === "function") {
                this.#stop();
            }
            this.#started = false;
        }
    }
}
class _Derived {
    #innerStore;
    #storesArray = [];
    #storeValues = [];
    #single;
    #fn;
    #started = false;
    #recalculationPending = false;
    #queuedForRetry = false;
    #dependenciesPending = 0;
    #unsubscribers = [];
    // The first time you call `value` a subscription will be created, ensuring subsequent accesses are fast
    #valueSubscriber = undefined;
    constructor(stores, fn, equalityCheck) {
        this.#innerStore = new _Writable(undefined, (_) => this.#start(), equalityCheck);
        const isArray = Array.isArray(stores);
        this.#single = !isArray;
        this.#storesArray = isArray ? stores.map(convertStore) : [convertStore(stores)];
        this.#fn = fn;
    }
    subscribe(subscriber, invalidate) {
        return this.#innerStore.subscribe(subscriber, invalidate);
    }
    get value() {
        this.#valueSubscriber ??= this.subscribe(NOOP);
        return this.#innerStore.value;
    }
    get dirty() {
        return this.#recalculationPending || this.#dependenciesDirty();
    }
    #start() {
        if (this.#started)
            return;
        for (const [index, store] of this.#storesArray.entries()) {
            const unsub = untrack(() => store.subscribe((v) => {
                this.#storeValues[index] = v;
                this.#dependenciesPending &= ~(1 << index);
                this.#recalculationPending = true;
                if (this.#started) {
                    this.#sync(false);
                }
            }, () => (this.#dependenciesPending |= 1 << index)));
            if (typeof unsub === "function") {
                this.#unsubscribers.push(unsub);
            }
        }
        this.#started = true;
        this.#sync(true);
        return () => this.#stop();
    }
    #stop() {
        for (const unsub of this.#unsubscribers) {
            unsub();
        }
        this.#unsubscribers = [];
        this.#started = false;
    }
    #sync(force) {
        if (!force) {
            // Exit early if no recalculation required
            if (!this.#recalculationPending) {
                return;
            }
            // If any dependencies are still dirty, queue this store to be retried
            if (this.#dependenciesDirty()) {
                if (!this.#queuedForRetry) {
                    derivedStoresToRetry.push(() => {
                        this.#queuedForRetry = false;
                        this.#sync(false);
                    });
                    this.#queuedForRetry = true;
                }
                return;
            }
        }
        const newValue = this.#fn((this.#single ? this.#storeValues[0] : this.#storeValues));
        this.#recalculationPending = false;
        this.#innerStore.set(newValue);
    }
    #dependenciesDirty() {
        return this.#dependenciesPending !== 0 || this.#storesArray.some((s) => s.dirty);
    }
}
function convertStore(store) {
    if (store === undefined) {
        const err = new Error();
        console.log(err.stack);
    }
    if ("dirty" in store && "value" in store) {
        return store;
    }
    let value;
    store.subscribe((v) => (value = v));
    return {
        subscribe: (start, invalidate) => store.subscribe(start, invalidate),
        get dirty() {
            return false;
        },
        get value() {
            return value;
        },
    };
}
